import { useState, useEffect, useMemo } from "react";
import { ValidationConfig, validateField } from "@/utils/validation";
import {
  ROUND_TRIP_NUMBER_LOCALE,
  formatMonetaryDisplayValue,
  formatNumber,
  getCurrencySign,
  currencyCodeFromSetting,
  parseLocaleNumericInput
} from "@/utils/numbers";
import { getCurrentLocale } from "@/utils/locale";
import useBoundStore from "@/services/store/BoundStore";

interface UseInputWithValidationOptions {
  initialValue: string;
  validation?: ValidationConfig;
  isMonetary?: boolean;
  isNumericOnly?: boolean;
  maxDigits?: number;
  isPretty?: boolean;
  currencySymbol?: string;
  onSave?: (value: string) => void;
}

/**
 * What the monetary input accepts from a keystroke. Exported so the locale tests
 * can assert the other half of the contract: that whatever the pinned formatter
 * emits still gets past this guard. A space-grouped value does not.
 */
export const MONETARY_INPUT_PATTERN = /^-?[\d,.]*$/;

export const useInputWithValidation = ({
  initialValue,
  validation,
  isMonetary = false,
  isNumericOnly = false,
  maxDigits,
  isPretty = false,
  currencySymbol,
  onSave
}: UseInputWithValidationOptions) => {
  const [value, setValue] = useState<string>(initialValue);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  // Deliberately NOT locale-aware, because making it so re-corrupts the value:
  // it is applied to `value` on every render, and in a dot-grouping locale a
  // second pass strips the canonical decimal point, turning 25000.50 into
  // 2500050 — the very defect being fixed, reintroduced from the other side.
  //
  // So every caller must hand it a value that is already canonical. All three
  // do: `handleBlur` and EditableInput's own render both pass `value` state, and
  // `handleChange` passes the parsed `valueToValidate` rather than the
  // keystroke. That last one was the bug — feeding it the raw "25000,50" made
  // validation read 2500050 while storage held 25000.50. It was dormant only
  // because no monetary field wires min/max (currency validation is isNaN-only),
  // so keep this invariant in mind before routing any new caller through here.
  const getRawValue = (val: string): string => {
    if (isMonetary) return val.replace(/,/g, "");
    return val;
  };

  // Both getters are named rawValue, not value: the hook already holds a
  // `value` state, and shadowing it here would read as though these formatted
  // that.

  // What an input being typed into renders. Pinned, not the viewer's locale,
  // because handleChange and getRawValue parse this string straight back — and
  // without the sign, which that same guard would reject.
  const getMonetaryInputValue = (rawValue: string): string =>
    formatMonetaryDisplayValue(
      rawValue,
      {
        isMonetary,
        isPretty,
        currencySymbol,
        omitCurrencySign: true,
        // Ungrouped so the field never inserts a separator of its own on top of
        // the canonical decimal point it already writes back. It does not make
        // every separator in the box the user's own — the decimal point is the
        // field's — which is why the parse reads a lone dot positionally rather
        // than by locale.
        omitGrouping: true
      },
      ROUND_TRIP_NUMBER_LOCALE
    );

  // Subscribed rather than read through getState(): the account currency lands
  // after first paint, and without a subscription the field never re-renders to
  // pick it up. Gated on isMonetary so a field that cannot use it never
  // subscribes at all.
  const accountCurrency = useBoundStore((state) =>
    isMonetary
      ? state.settings.accountSettings["account_currency"]?.value
      : undefined
  );

  // The sign the input dropped, for the caller to render beside the field. It
  // is never parsed back, so it follows the viewer's locale.
  const currencySign = useMemo(
    () =>
      getCurrencySign(
        {
          isMonetary,
          isPretty,
          currencySymbol,
          // `?? undefined` rather than widening the field: the setting reader
          // returns null, the formatter takes string | undefined.
          currencyCode: currencyCodeFromSetting(accountCurrency) ?? undefined
        },
        getCurrentLocale()
      ),
    [isMonetary, isPretty, currencySymbol, accountCurrency]
  );

  // What a surface that is only showing the value renders — the collapsed text
  // of a field nobody is editing. Nothing parses it back, so it reads in the
  // viewer's own locale.
  const getMonetaryDisplayValue = (rawValue: string): string =>
    formatMonetaryDisplayValue(rawValue, {
      isMonetary,
      isPretty,
      currencySymbol
    });

  const validateInput = (newValue: string): boolean => {
    if (validation) {
      const validationError = validateField(getRawValue(newValue), validation);
      if (validationError) {
        setError(validationError);
        return false;
      }
    }
    setError(null);
    return true;
  };

  const handleChange = (newValue: string): void => {
    // What the tail-end validateInput call below judges. For a monetary field
    // that is the CANONICAL value, not the keystroke: validateInput runs
    // getRawValue, which is locale-blind, so handing it the raw "25000,50" made
    // validation read 2500050 while storage held 25000.50.
    let valueToValidate = newValue;
    if (isMonetary) {
      // Only allow an optional leading minus, numbers, commas, and decimal points
      if (!MONETARY_INPUT_PATTERN.test(newValue)) return;

      // Read the separators by the viewer's locale, not as grouping always.
      // Stripping commas unconditionally is what stored 25000,50 as 2500050.
      const rawValue = parseLocaleNumericInput(newValue);
      valueToValidate = rawValue;
      if (rawValue === "" || rawValue === "-") {
        setValue(rawValue);
        // Clear as well as return. This path skips the validateInput call at
        // the end of handleChange, which is the only thing that clears an
        // error while typing — so a user who blurred on a separator-only entry,
        // saw "Invalid number", then backspaced the field empty would keep
        // staring at the error until their next blur. Harmless before this
        // change, because nothing could set an error on these fields.
        setError(null);
        return;
      }

      // Pre-existing, and now unreachable: parseLocaleNumericInput returns at
      // most one dot by construction, whichever branch it takes. Left in place
      // rather than deleted, since it costs nothing and is the right guard if
      // that ever stops holding — but do not count on it to refuse anything.
      const decimalCount = (rawValue.match(/\./g) || []).length;
      if (decimalCount > 1) return;

      if (maxDigits && rawValue.replace(/[-.]/g, "").length > maxDigits) return;

      // Handle decimal values differently - preserve exact decimal input
      if (rawValue.includes(".")) {
        const [integerPart, decimalPart] = rawValue.split(".");
        if (integerPart === "") {
          setValue(rawValue);
        } else {
          const numValue = parseFloat(integerPart);
          if (!isNaN(numValue)) {
            const { value: formattedInteger } = formatNumber(
              numValue,
              0,
              false,
              false,
              false,
              ROUND_TRIP_NUMBER_LOCALE,
              isPretty || isMonetary
            );
            setValue(`${formattedInteger}.${decimalPart}`);
          } else {
            setValue(rawValue);
          }
        }
      } else {
        const numValue = parseFloat(rawValue);
        if (!isNaN(numValue)) {
          const { value: formattedValue } = formatNumber(
            numValue,
            0,
            isMonetary,
            false,
            false,
            ROUND_TRIP_NUMBER_LOCALE,
            isPretty || isMonetary
          );
          setValue(formattedValue);
        } else {
          setValue(rawValue);
        }
      }
    } else if (isNumericOnly) {
      if (!/^\d*$/.test(newValue)) return;
      if (maxDigits && newValue.length > maxDigits) return;
      setValue(newValue);
    } else {
      setValue(newValue);
    }
    validateInput(valueToValidate);
  };

  const handleBlur = (): void => {
    const currentValue = getRawValue(value);
    // A separator-only or sign-only entry is not a number: "." arises from a
    // lone comma in a comma-decimal locale, and "." / "-" / "-." can each be
    // typed directly in any locale. Saving one sends a non-numeric literal to a
    // financial field, and several monetary callers pass no validation config to
    // catch it. Empty is unaffected and still saves — that is how a field is
    // cleared — because Number("") is 0, which is finite.
    if (isMonetary && !Number.isFinite(Number(currentValue))) {
      // Say so rather than returning silently. The collapsed field would
      // otherwise keep rendering the literal the user typed, with nothing
      // indicating it was never saved. "Invalid number" matches the fallback
      // commonRules.number already uses.
      setError("Invalid number");
      return;
    }
    if (!validateInput(currentValue)) {
      return;
    }
    onSave?.(currentValue);
  };

  return {
    value,
    error,
    setError,
    setValue,
    handleChange,
    handleBlur,
    getRawValue,
    getMonetaryDisplayValue,
    getMonetaryInputValue,
    currencySign,
    validateInput
  };
};
